import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useBannerContext } from "oolib";
import { parseSpecialConfigSyntax } from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGenErrorMsgBanners } from "@/utils/useGenErrorMsgBanners";

/**
 * Fires a direct-data-import job (POST directDataImportViaQueues). The actual
 * import runs on BE queues — success here means "job created", not "data
 * imported". Used by both submit paths: first import (ContentTypeTitleModal)
 * and reimport (useReimportWithTypeChanges).
 */
export const useDirectImportData = ({ skipPostImportNavigation = false } = {}) => {
  const navigate = useNavigate();
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");
  const queryClient = useQueryClient();
  const { SET_ALERT_BANNER } = useBannerContext();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation({
    mutationFn: async ({ fileNameInGcs, config, sheetName, integrationActivation, pendingDirectDataImportId }) => {
      const response = await axios.post(
        `/api/content/directDataImportViaQueues/${config.tplConfigs.contentType}`,
        {
          fileNameInGcs,
          config,
          // The worker re-reads the file from GCS, so it needs the same sheet
          // the user previewed — without this we'd import sheet 1 regardless.
          ...(sheetName && { sheetName }),
          // When the rows came from an integration (from any screen), the BE
          // worker uses this to create/refresh the dataset's integration link
          // on the first batch.
          ...(integrationActivation ? { integrationActivation } : {}),
          // Present when resuming a saved draft — the BE worker deletes that
          // draft on the first batch, atomically within the tpl-creation transaction.
          ...(pendingDirectDataImportId ? { pendingDirectDataImportId } : {}),
        }
      );

      return response.data;
    },
    onSuccess: async (data, mutationArgs) => {
      console.log("Data Import Job Created Successfully", data);

      // All of the below would only work if we were actually getting the
      // imported data in the response. but now we aren't, since we are using queues.

      // SET_ALERT_BANNER("Data Imported Successfully!", "green", 4000);
      // updateRolesConfigCacheForNewlyCreatedTpl({
      //   queryClient,
      //   updatedRolesConfig: data?.templateResult?.updatedRolesConfig,
      // });

      // updateAllTplsCache({
      //   queryClient,
      //   updatedTpl: data?.templateResult?.tpl,
      // });

      // Imports launched from the Integrations tab redirect back to
      // /integrations so the user sees their card flip to "Active". This
      // overrides the standard self-managed post-register redirect. Keyed on
      // the launch, not on integrationActivation: Datasets-tab imports of
      // integration data carry that too, and must not leave the page.
      if (mutationArgs.redirectToIntegrations && !skipPostImportNavigation) {
        navigate("/integrations");
        return;
      }

      const redirLinkForSelfManaged =
        SELF_MANAGED_BASE_CONFIGS?.batchRegisterContent
          ?.redirectAfterRegisterLink;
      if (redirLinkForSelfManaged && !skipPostImportNavigation) {
        navigate(
          parseSpecialConfigSyntax({
            config: redirLinkForSelfManaged,
            content: {
              contentType: mutationArgs.config.tplConfigs.contentType,
            },
          })
        );
      }
    },
    onError: (error) => {
      console.error("Error in data import:", error);
      genErrorMsgBanners({ err: error });
    },
  });
};
